import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  memo,
  useRef,
} from "react";
import { Box, Typography } from "@mui/material";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { isArray, isBoolean } from "lodash";
import { $APP_NAME, ROLES } from "src/constants";

import {
  useDownloadAwbForShipmentsMutation,
  useLazyValidateWarehousesForOrdersQuery,
} from "src/_api/mapitShipApi";

import { useGetBoxesQuery } from "src/_api/shipmentBox";
import { openConfirmDialog } from "src/_store/entities/confirmationDialog";
import { useFormatMapitShipOrders } from "src/modules/Order/components/mapitship-orders-format-hooks";

import {
  OrderDialog,
  OrdersFilter,
  ShipmentStatusTabs,
  AdminActions,
  MerchantActions,
  OrdersDataTable,
  AssignOrdersSection,
  ConfirmOrdersForShipmentDialog,
  ExportMapitShipOrders,
} from "./components";
import ValidateWarehousesDialog from "./ValidateWarehousesDialog";
import BulkAssignWizard from "src/_modules/MapitShip/views/PriceCalculator/components/BulkAssign/BulkAssignWizard";
import {
  useOrderParams,
  useOrdersData,
  useOrdersCount,
  useOrderSelection,
  useOrderDialog,
} from "./hooks";

/**
 * MapitShipOrders - Refactored for performance and readability
 *
 * Key optimizations:
 * 1. Split into smaller focused components (AdminActions, MerchantActions, OrdersDataTable)
 * 2. Custom hooks for state management to prevent re-renders
 * 3. Memoized column definitions
 * 4. useCallback for all event handlers
 * 5. Proper useMemo dependencies
 * 6. Separated concerns into logical units
 */
function MapitShipOrders() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const { profile } = useSelector((state) => state.entities.profile);

  // ============ URL-synced Parameters ============
  const {
    params, // Applied params (synced to URL)
    filterParams, // Local filter state (for inputs)
    setParams,
    handleFilterChange,
    applyFilters,
  } = useOrderParams();

  // Ref to always access current params (for callbacks that need current value)
  const paramsRef = useRef(params);
  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  // Use params.status as single source of truth (updates immediately on tab change)
  const status = params?.status || "ORDER_PENDING";

  // ============ Orders Data ============
  const {
    orders,
    isLoading: ordersLoading,
    isFetching: ordersFetching,
    rowCount,
    fetchOrders,
  } = useOrdersData(profile);

  // Initial fetch
  useEffect(() => {
    fetchOrders(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // ============ Orders Count ============
  const { ordersCount, isLoading: ordersCountLoading } =
    useOrdersCount(profile);

  // ============ Order Selection ============
  const {
    selectedOrdersIds,
    selectedOrders,
    setSelectedOrdersIds,
    clearSelection,
  } = useOrderSelection(orders?.docs);

  // ============ Order Dialog ============
  const refreshOrders = useCallback(() => {
    fetchOrders(paramsRef.current);
  }, [fetchOrders]);

  const {
    selectedOrder,
    openOrderDialog,
    isEditing,
    openDialog: openOrderDialogHandler,
    closeDialog: closeOrderDialogHandler,
  } = useOrderDialog(searchParams, refreshOrders);

  // ============ Pagination & Tab Handlers ============
  const handlePageChange = useCallback(
    (page) => {
      if (!ordersLoading && !ordersFetching) {
        setParams((prev) => {
          const newParams = { ...prev, page };
          // Use queueMicrotask to fetch after state update
          queueMicrotask(() => fetchOrders(newParams));
          return newParams;
        });
      }
    },
    [ordersLoading, ordersFetching, setParams, fetchOrders],
  );

  const handleTabChange = useCallback(
    (e) => {
      const newStatus = e.target.value;
      // Update params immediately (this updates the UI right away)
      setParams((prev) => {
        const newParams = { ...prev, status: newStatus, page: 1 };
        // Use queueMicrotask to fetch after state update
        queueMicrotask(() => fetchOrders(newParams));
        return newParams;
      });
    },
    [setParams, fetchOrders],
  );

  const handleFilterSubmit = useCallback(() => {
    // Apply filters and fetch - applyFilters handles page reset and URL sync
    applyFilters((newParams) => {
      fetchOrders(newParams);
    });
  }, [applyFilters, fetchOrders]);

  // ============ Shipment Creation ============
  const [shipmentType, setShipmentType] = useState(null);
  const [openCreateShipmentDialog, setOpenCreateShipmentDialog] =
    useState(false);
  const [openValidateWarehousesDialog, setOpenValidateWarehousesDialog] =
    useState(false);
  const [warehouseIssues, setWarehouseIssues] = useState([]);
  const [selectedReturnOrder, setSelectedReturnOrder] = useState([]);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [skipFormatting, setSkipFormatting] = useState(true);
  // When true, the preflight below opens the bulk wizard instead of navigating
  // to the one-by-one assign page.
  const [bulkMode, setBulkMode] = useState(false);
  const [openBulkAssign, setOpenBulkAssign] = useState(false);

  const [triggerValidateWarehouses, { isFetching: isValidatingWarehouses }] =
    useLazyValidateWarehousesForOrdersQuery();

  // Boxes for formatting
  const { data: boxes } = useGetBoxesQuery(
    {},
    { skip: profile?.merchant?.role !== ROLES.merchant },
  );

  const defaultBox = useMemo(
    () => boxes?.find((box) => box.default) ?? null,
    [boxes],
  );

  const { ordersWithHiddenCards, isLoading: formattingLoading } =
    useFormatMapitShipOrders(
      selectedOrders,
      defaultBox,
      skipFormatting,
      warehouseIssues,
    );
  const handleCloseAssignDialog = useCallback(() => {
    setOpenAssignDialog(false);
    setSkipFormatting(true);
  }, []);

  const runShipmentPreflight = useCallback(async () => {
    if (!selectedOrdersIds.length) return;

    try {
      const res = await triggerValidateWarehouses({
        ids: selectedOrdersIds,
        isAdmin: profile?.role === ROLES.admin,
      }).unwrap();
      if (Array.isArray(res) && res.length) {
        setWarehouseIssues(res);
        setOpenValidateWarehousesDialog(true);
      }
      setSkipFormatting(false);
    } catch (e) {
      // Error handling
    }
  }, [selectedOrdersIds, triggerValidateWarehouses, profile?.role]);

  const handleCreateShipment = useCallback(() => {
    setBulkMode(false);
    return runShipmentPreflight();
  }, [runShipmentPreflight]);

  // Bulk assign shares the same preflight: orders missing boxes/weights must be
  // fixed first, otherwise every one of them fails the availability pass.
  const handleBulkAssign = useCallback(() => {
    setBulkMode(true);
    return runShipmentPreflight();
  }, [runShipmentPreflight]);

  // Once the orders are ready: open the bulk wizard, or navigate to the
  // one-by-one price calculator.

  useEffect(() => {
    if (
      !skipFormatting &&
      !openAssignDialog &&
      isArray(ordersWithHiddenCards)
    ) {
      if (ordersWithHiddenCards.length) {
        setOpenAssignDialog(true);
      } else if (bulkMode) {
        setOpenBulkAssign(true);
        setSkipFormatting(true);
      } else {
        const basePath =
          profile?.merchant?.role === ROLES.merchant
            ? "/merchant/mapit-ship/shipments/price-calculator/list"
            : "/admin/shipments/price-calculator/list";
        navigate(`${basePath}?ordersIds=${selectedOrdersIds}`);
      }
    }
  }, [
    skipFormatting,
    openAssignDialog,
    ordersWithHiddenCards,
    navigate,
    profile,
    selectedOrdersIds,
    bulkMode,
  ]);

  const handleCreateReturnShipment = useCallback((order) => {
    setSelectedReturnOrder([order]);
    setShipmentType("RETURN");
    setOpenCreateShipmentDialog(true);
  }, []);

  const handleCloseShipmentDialog = useCallback(
    (unSelectOrders) => {
      setOpenCreateShipmentDialog(false);
      setShipmentType(null);
      setSelectedReturnOrder([]);
      if (isBoolean(unSelectOrders) && unSelectOrders) {
        clearSelection();
      }
    },
    [clearSelection],
  );

  const handleAssignSuccess = useCallback(() => {
    if (bulkMode) {
      setOpenBulkAssign(true);
      return;
    }
    const basePath =
      profile?.role === ROLES.admin
        ? "/admin/shipments/price-calculator/list"
        : "/merchant/mapit-ship/shipments/price-calculator/list";
    navigate(`${basePath}?ordersIds=${selectedOrdersIds}`);
  }, [bulkMode, navigate, profile?.role, selectedOrdersIds]);

  const handleBulkAssignFinished = useCallback(() => {
    setOpenBulkAssign(false);
    setBulkMode(false);
    clearSelection();
    fetchOrders(paramsRef.current);
  }, [clearSelection, fetchOrders]);

  // ============ Import Dialog ============
  const [openImportMapitShipDialog, setOpenImportMapitShipDialog] =
    useState(false);

  const handleOpenImportDialog = useCallback(() => {
    setOpenImportMapitShipDialog(true);
  }, []);

  const handleCloseImportDialog = useCallback(() => {
    setOpenImportMapitShipDialog(false);
  }, []);

  const handleImportSuccess = useCallback(() => {
    fetchOrders(paramsRef.current);
  }, [fetchOrders]);

  // ============ Clone Orders Dialog ============
  const [openCloneDialog, setOpenCloneDialog] = useState(false);

  const handleOpenCloneDialog = useCallback(() => {
    setOpenCloneDialog(true);
  }, []);

  const handleCloseCloneDialog = useCallback(() => {
    setOpenCloneDialog(false);
  }, []);

  const handleCloneSuccess = useCallback(() => {
    fetchOrders(paramsRef.current);
    clearSelection();
  }, [fetchOrders, clearSelection]);

  // ============ Download AWB ============
  const [downloadAwbForShipments, { isLoading: isDownloadingAwb }] =
    useDownloadAwbForShipmentsMutation();

  const handleDownloadAwb = useCallback(async () => {
    if (selectedOrdersIds.length > 25) {
      dispatch(
        openConfirmDialog({
          title: t("Confirm"),
          content: t("You can't download more than twenty-five shipments."),
        }),
      );
      return;
    }

    const pdfBlob = await downloadAwbForShipments({
      orderIds: selectedOrdersIds,
    }).unwrap();

    const url = window.URL.createObjectURL(pdfBlob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "awb.pdf");
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [selectedOrdersIds, downloadAwbForShipments, dispatch, t]);


  // ============ Validate Warehouses Dialog ============
  const handleCloseValidateDialog = useCallback(() => {
    setOpenValidateWarehousesDialog(false);
    setWarehouseIssues([]);
  }, []);

  // ============ Table Handlers ============
  const handleRowClick = useCallback(
    (row) => {
      openOrderDialogHandler(row);
    },
    [openOrderDialogHandler],
  );

  // ============ Derived State ============
  const isAdmin = profile?.role === ROLES.admin;

  // ============ Render ============
  return (
    <>
      <Helmet>
        <title>Shipments | {$APP_NAME}</title>
      </Helmet>

      <Typography variant="h2" sx={{mb: 1.5}}>{t("Orders")}</Typography>

      {/* Assign Orders Dialog */}
      <AssignOrdersSection
        open={openAssignDialog}
        onClose={handleCloseAssignDialog}
        selectedOrders={selectedOrders}
        ordersWithHiddenCards={ordersWithHiddenCards}
        isLoading={formattingLoading}
        onSuccess={handleAssignSuccess}
        warehouseIssues={warehouseIssues}
      />

      {/* Bulk assign: one shipping company for every selected order */}
      {!isAdmin && (
        <BulkAssignWizard
          open={openBulkAssign}
          orderIds={selectedOrdersIds}
          onClose={() => {
            setOpenBulkAssign(false);
            setBulkMode(false);
          }}
          onFinished={handleBulkAssignFinished}
        />
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        mb={1}
      >
        {/* Action Buttons */}
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
          {/* Admin Actions */}
          {isAdmin && (
            <AdminActions
              status={status}
              selectedOrdersIds={selectedOrdersIds}
              selectedOrders={selectedOrders}
              isValidatingWarehouses={isValidatingWarehouses}
              formattingLoading={formattingLoading}
              isDownloadingAwb={isDownloadingAwb}
              onCreateShipment={handleCreateShipment}
              onDownloadAwb={handleDownloadAwb}
            />
          )}

          {/* Merchant Actions */}
          {!isAdmin && (
            <MerchantActions
              status={status}
              selectedOrdersIds={selectedOrdersIds}
              selectedOrders={selectedOrders}
              selectedReturnOrder={selectedReturnOrder}
              shipmentType={shipmentType}
              isValidatingWarehouses={isValidatingWarehouses}
              formattingLoading={formattingLoading}
              isDownloadingAwb={isDownloadingAwb}
              openImportDialog={openImportMapitShipDialog}
              openCreateShipmentDialog={openCreateShipmentDialog}
              onOpenOrderDialog={() => openOrderDialogHandler()}
              onOpenImportDialog={handleOpenImportDialog}
              onCloseImportDialog={handleCloseImportDialog}
              onImportSuccess={handleImportSuccess}
              onCreateShipment={handleCreateShipment}
              onBulkAssign={handleBulkAssign}
              onDownloadAwb={handleDownloadAwb}
              onCloseShipmentDialog={handleCloseShipmentDialog}
              openCloneDialog={openCloneDialog}
              onOpenCloneDialog={handleOpenCloneDialog}
              onCloseCloneDialog={handleCloseCloneDialog}
              onCloneSuccess={handleCloneSuccess}
            />
          )}
        </Box>

        {/* Export & Filter */}
        <Box display="flex" alignItems="center" flex={1} justifyContent="flex-end" gap={1}>
          <ExportMapitShipOrders
            status={status}
            params={params}
            isAdmin={isAdmin}
          />

          {/* Filter Button */}
          <OrdersFilter
            params={filterParams}
            setParams={setParams}
            onSubmit={handleFilterSubmit}
            onChange={handleFilterChange}
            isLoading={ordersLoading || ordersFetching}
            status={status}
          />
        </Box>
      </Box>

      {/* Order Dialog */}
      <OrderDialog
        orderId={selectedOrder}
        open={openOrderDialog}
        onClose={closeOrderDialogHandler}
        isEditing={isEditing}
        defaultBox={defaultBox}
      />

      {openCreateShipmentDialog && (
        <ConfirmOrdersForShipmentDialog
          open={openCreateShipmentDialog}
          onClose={handleCloseShipmentDialog}
          selectedOrders={
            shipmentType === "RETURN" ? selectedReturnOrder : selectedOrders
          }
          shipmentType={shipmentType}
        />
      )}

      {/* Status Tabs */}
      <ShipmentStatusTabs
        value={status}
        onChange={handleTabChange}
        ordersCount={ordersCount}
        ordersCountLoading={ordersCountLoading}
      />

      {/* Validate Warehouses Dialog */}
      {/* {openValidateWarehousesDialog && (
        <ValidateWarehousesDialog
          open={openValidateWarehousesDialog}
          onClose={handleCloseValidateDialog}
          issues={warehouseIssues}
        />
      )} */}

      {/* Data Table */}
      <OrdersDataTable
        orders={orders}
        status={status}
        profile={profile}
        params={params}
        rowCount={rowCount}
        isLoading={ordersLoading || ordersFetching}
        selectedOrdersIds={selectedOrdersIds}
        onRowClick={handleRowClick}
        onPageChange={handlePageChange}
        onSelectionChange={setSelectedOrdersIds}
        onCreateReturn={handleCreateReturnShipment}
      />
    </>
  );
}

export default memo(MapitShipOrders);
